import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User as UserIcon, ShoppingBag } from 'lucide-react';
import { Logo } from './Logo';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
export function SiteNav() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const {
    user
  } = useAuth();
  const {
    itemCount,
    openCart
  } = useCart();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  const navLinks = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'About',
    path: '/about'
  }, {
    name: 'Services',
    path: '/services'
  }, {
    name: 'Training',
    path: '/training'
  }, {
    name: 'Shop',
    path: '/shop'
  }, {
    name: 'Books',
    path: '/books'
  }];
  const isActive = (path: string) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#F5F5F0] ${isScrolled ? 'py-3 shadow-sm border-b border-[#E5E5E0]' : 'py-5 border-b border-transparent'}`} data-id="element-2772">
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between gap-6" data-id="element-2773">
        {/* Logo */}
        <Link to="/" className="flex items-center group shrink-0" aria-label="Home" data-id="element-2774">
          <img src="https://cdn.magicpatterns.com/uploads/p9vvjChsgrbXabFRRLLULN/Logo.png" alt="Structured Nutrition" className="w-16 h-16 object-contain transition-transform group-hover:scale-105" data-id="element-2775" />
        </Link>

        {/* Desktop Nav (centered) */}
        <nav className="hidden lg:flex items-center gap-8 flex-1 justify-center" data-id="element-2776">
          {navLinks.map(link => {
          const active = isActive(link.path);
          return <Link key={link.name} to={link.path} className="relative text-[11px] font-bold uppercase tracking-[0.2em] transition-colors py-2" data-id="element-2777">
                <span className={active ? 'text-tealslate' : 'text-[#1F1F1F] hover:text-tealslate transition-colors'} data-id="element-2778">
                  {link.name}
                </span>
                {active && <span className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-tealslate" data-id="element-2779" />}
              </Link>;
        })}
          <Link to="/contact" className="relative text-[11px] font-bold uppercase tracking-[0.2em] transition-colors py-2" data-id="element-2780">
            <span className={isActive('/contact') ? 'text-tealslate' : 'text-[#1F1F1F] hover:text-tealslate transition-colors'} data-id="element-2781">
              Contact
            </span>
            {isActive('/contact') && <span className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-tealslate" data-id="element-2782" />}
          </Link>
        </nav>

        {/* Right side actions */}
        <div className="hidden lg:flex items-center gap-3 shrink-0" data-id="element-2783">
          <Link to={user ? '/account' : '/login'} className="inline-flex items-center justify-center w-9 h-9 text-[#1F1F1F] hover:text-tealslate transition-colors" aria-label={user ? 'Account' : 'Sign in'} data-id="element-2784">
            <UserIcon className="w-[19px] h-[19px]" data-id="element-2785" />
          </Link>
          <button onClick={openCart} className="relative inline-flex items-center justify-center w-9 h-9 text-[#1F1F1F] hover:text-tealslate transition-colors" aria-label={`Open cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`} data-id="element-2786">
            <ShoppingBag className="w-[19px] h-[19px]" data-id="element-2787" />
            {itemCount > 0 && <span className="absolute -top-0.5 -right-0.5 bg-tealslate text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center" data-id="element-2788">
                {itemCount}
              </span>}
          </button>
          <Link to="/contact" className="ml-2 inline-flex items-center px-5 py-3 border border-tealslate text-tealslate text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-tealslate hover:text-white transition-colors" data-id="element-2789">
            Book a Consultation
          </Link>
        </div>

        {/* Mobile actions */}
        <div className="lg:hidden flex items-center gap-1" data-id="element-2790">
          <button onClick={openCart} className="relative inline-flex items-center justify-center w-10 h-10 text-[#1F1F1F]" aria-label={`Open cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`} data-id="element-2791">
            <ShoppingBag className="w-5 h-5" data-id="element-2792" />
            {itemCount > 0 && <span className="absolute top-0.5 right-0.5 bg-tealslate text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center" data-id="element-2793">
                {itemCount}
              </span>}
          </button>
          <button className="text-[#1F1F1F] p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu" data-id="element-2794">
            {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-2795" /> : <Menu className="w-6 h-6" data-id="element-2796" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileMenuOpen && <div className="lg:hidden absolute top-full left-0 w-full bg-[#F5F5F0] border-t border-[#E5E5E0] shadow-lg" data-id="element-2797">
          <nav className="flex flex-col py-2" data-id="element-2798">
            {navLinks.map(link => <Link key={link.name} to={link.path} className={`px-6 py-4 text-sm font-bold uppercase tracking-[0.2em] transition-colors ${isActive(link.path) ? 'text-tealslate bg-[#EAEAE3]' : 'text-[#1F1F1F] hover:bg-[#EAEAE3]'}`} data-id="element-2799">
                {link.name}
              </Link>)}
            <Link to="/contact" className={`px-6 py-4 text-sm font-bold uppercase tracking-[0.2em] transition-colors ${isActive('/contact') ? 'text-tealslate bg-[#EAEAE3]' : 'text-[#1F1F1F] hover:bg-[#EAEAE3]'}`} data-id="element-2800">
              Contact
            </Link>
            <Link to={user ? '/account' : '/login'} className="px-6 py-4 text-sm font-bold text-[#1F1F1F] uppercase tracking-[0.2em] hover:bg-[#EAEAE3] transition-colors border-t border-[#E5E5E0] flex items-center gap-2" data-id="element-2801">
              <UserIcon className="w-4 h-4" data-id="element-2802" />
              {user ? 'Account' : 'Sign In'}
            </Link>
            <div className="px-6 py-4 border-t border-[#E5E5E0]" data-id="element-2803">
              <Link to="/contact" className="block w-full text-center px-5 py-3 border border-tealslate text-tealslate text-xs font-bold uppercase tracking-[0.2em] hover:bg-tealslate hover:text-white transition-colors" data-id="element-2804">
                Book a Consultation
              </Link>
            </div>
          </nav>
        </div>}
    </header>;
}